// UnitView.js
//
// The unit view of the file review dialog (Spec 2 section 6.4): what Decode did to one
// table of a file, step by step, with the person's controls on each step. The dialog holds
// the live file, unit, table and batch documents and passes them in; this view reads them
// and writes only through the cleaning gestures, each one transaction that judges
// editability on the live documents again.
//
//     header      the file › table breadcrumb, the table's name, status, rows, warnings and
//                 approver, Download, and Approve or "Un-approve to make changes"
//     notices     why the table cannot be edited now, what stands between it and approval
//                 (the gate its last run wrote on the file's ledger, which the approve
//                 gesture tests), and "Decode is applying your change…" while it is worked on
//     steps       1 Source · 2 Columns · 3 Dates and numbers · a step per mutating rule ·
//                 a step per classified column · a step per skip rule · Rows with no
//                 {identifier} and Reporting period (time series) · Rows after cleaning ·
//                 once a time-series table's unit has landed, What this file changed in
//                 {table} · Values other files overruled · Rows corrected by answers
//
// The steps are the table's chain as the backend builds it (unitChainProcessIds, the twin
// of chains.py unit_processes), not the records the unit happens to carry: a record left
// by a rule since removed from the table is not a step. The landed steps show what the
// landing wrote on the unit (UnitLandedSteps).
//
// Approving, un-approving and changing the file's own decisions go through the dialog
// (onUnitApproval, onFileChange), which confirms a change that would un-approve the file
// and closes that confirmation, unsent, if its target turns busy or approved meanwhile.
// The steps' own edits of the table go through runUnitChange here. A step's draft is set
// aside when the table stops being editable, unless it is the draft whose change made it
// so: the view then moves on its draft epoch (UnitStepFrame's useStepDraft).

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import { approveUnit, gestureFailureMessage, unapproveUnit } from '../../../services/cleaningGestures';
import { downloadGcsFileByPrefix } from '../../../services/api_utils';
import {
  UNIT_LABEL,
  fileEditBlock,
  unitChainProcessIds,
  unitEditBlock,
} from '../../../utils/ingestionStatus';
import { tableScope } from '../../../utils/blobPaths';
import { countNoun } from '../../../utils/pluralise';
import { isTimeSeriesTable } from '../../../utils/tableTypes';
import { VerdictAlert, isInFolder } from './UnitStepFrame';
import { PlaceholdersStep, PreviewStep, SourceStep } from './UnitReadOnlySteps';
import UnitColumnsStep from './UnitColumnsStep';
import { DatesAndNumbersStep, ReportingPeriodStep } from './UnitDatesSteps';
import UnitRuleStep from './UnitRuleStep';
import UnitClassifyStep from './UnitClassifyStep';
import {
  FileChangesStep,
  OverruledValuesStep,
  RowCorrectionsStep,
  useLandingBlob,
} from './UnitLandedSteps';
import { approvalLine } from './ingestionStatusDisplay';

// The storage families of a unit's two workbooks (functions/common/blob_paths.py): its
// base, cut by the file chain under its file, and its assertion, written by its own run.
const BASES = 'bases';
const ASSERTIONS = 'assertions';
// The families the landing stores a unit's records in when they are too large for its
// document: the diff together with the overruled values, and the correction records.
const DIFFS = 'diffs';
const ROW_CORRECTIONS = 'row_corrections';

// Props:
//   companyId, tableId, file, unit, table, batch   the live documents (batch: the file's
//                                                  batch document, or null)
//   currentUid                                     who is looking, for the approver's name
//   onBack                                         back to the file view
//   onOpenSheet(sheet, regionId)                   opens the sheet workspace on a table
//   onUnitApproval(run, { unitId, kind }), onFileChange(run, options)
//                                                  the dialog's runners, each answering
//                                                  whether the change was made
//   onToast(message)                               shows the person a failure
export default function UnitView({
  companyId,
  tableId,
  file,
  unit,
  table,
  batch = null,
  currentUid,
  onBack,
  onOpenSheet,
  onUnitApproval,
  onFileChange,
  onToast,
}) {
  const [approving, setApproving] = useState(false);

  const editBlock = unitEditBlock(unit.id, unit, file, batch, table);
  const fileBlock = fileEditBlock(file, batch);
  // Whether the file's deletion is set, live or failed. The view then shows the deletion and
  // disables every control (Spec 5 section 5): the edits through the edit blocks, which put
  // a deletion first, and the rest through this: Download, and the steps' controls that read
  // the unit's storage or move through what a step shows.
  const fileDeletionSet = file.deletion != null;

  // The steps' changes in flight from this view, and the draft epoch. The epoch moves on
  // each time the table stops being editable, judged once no change of this view is in
  // flight, and a step's draft from an earlier epoch is set aside (Spec 2 sections 6.1 and
  // 6.4, 00 N.4). A change this view sent makes the table busy too: the step that sent it
  // clears its own draft as the change returns, so that draft is never set aside, while a
  // draft left in another step, made against the table as it was before Decode runs it
  // again, is set aside like any other.
  const [ownChanges, setOwnChanges] = useState(0);
  const [draftEpoch, setDraftEpoch] = useState(0);
  const editableWhenLastIdle = useRef(!editBlock);
  useEffect(() => {
    if (ownChanges > 0) return;
    if (editBlock && editableWhenLastIdle.current) setDraftEpoch((epoch) => epoch + 1);
    editableWhenLastIdle.current = !editBlock;
  }, [editBlock, ownChanges]);

  // A step's change: `send` runs the gesture, and `onApplied` (the step clearing its
  // draft) runs on success in the same update as the change leaving flight, so the epoch
  // never sees a draft that was just applied. A refusal is shown in its own words.
  const runUnitChange = async (send, onApplied = () => {}) => {
    setOwnChanges((count) => count + 1);
    try {
      await send();
      onApplied();
    } catch (error) {
      onToast(gestureFailureMessage(error));
    } finally {
      setOwnChanges((count) => count - 1);
    }
  };

  // The gate as the unit's last run wrote it on the file's ledger, which is what the
  // approve gesture tests (Spec 2 section 5.2). A unit whose entry has not arrived yet is
  // the file chain's work in progress, and its edit block says so.
  const entry = file.units[unit.id];
  const gate = entry ? entry.gate : [];
  const ingest = unit.disposition === 'ingest';
  const busy = Boolean(unit.claim) || unit.status === 'Cleaning';
  const canApprove = ingest && !unit.approved && !editBlock && gate.length === 0;
  // Un-approving is allowed when nothing but the approval itself stands in the way of an
  // edit: the edit block the table would have if it were not approved.
  const unapproveBlock = unit.approved
    ? unitEditBlock(unit.id, { ...unit, approved: null }, file, batch, table)
    : '';
  const canUnapprove = Boolean(unit.approved) && !unapproveBlock;

  const verdicts = unit.verdicts || [];
  const verdictsOf = (processId) => verdicts.filter((verdict) => verdict.process === processId);

  const approve = async () => {
    setApproving(true);
    await onUnitApproval(
      () => approveUnit(companyId, tableId, file.id, unit.id),
      { unitId: unit.id, kind: 'approve' },
    );
    setApproving(false);
  };

  const unapprove = async () => {
    setApproving(true);
    await onUnitApproval(
      () => unapproveUnit(companyId, tableId, file.id, unit.id),
      { unitId: unit.id, kind: 'unapprove' },
    );
    setApproving(false);
  };

  // The assertion workbook once the table has one, otherwise the base it is read from.
  // Each is read only from the unit's own folder, and neither while the file's deletion
  // is set.
  const scope = tableScope(companyId, tableId);
  const assertionPath = unit.assertion?.path || null;
  const downloadPath = assertionPath || unit.base || null;
  const downloadFolder = assertionPath
    ? `${scope}/${ASSERTIONS}/${unit.id}/`
    : `${scope}/${BASES}/${file.id}/${unit.id}/`;
  const canDownload = !fileDeletionSet && isInFolder(downloadPath, downloadFolder);

  const download = async () => {
    const fileStem = (file.name || 'file').replace(/\.[^./]*$/, '');
    const fileName = `${fileStem} - ${unit.name}${assertionPath ? '' : ' (as read)'}.xlsx`;
    try {
      await downloadGcsFileByPrefix(downloadPath, fileName);
    } catch (error) {
      console.error('Failed to download a table workbook:', error);
      onToast('The workbook could not be downloaded. Try again.');
    }
  };

  // What the landing wrote on the unit (Spec 4 sections 3.2 and 8), shown once the unit has
  // landed; a raw table's units land with none of it. A record too large for the unit's
  // document is a blob in the unit's own folder that the document names ({ path }): the
  // diff and the overruled values share one, read once here for both of their steps.
  const landed = unit.status === 'Completed' && isTimeSeriesTable(table);
  const changesPath = landed && typeof unit.diff?.path === 'string' ? unit.diff.path : null;
  const correctionsPath = landed && typeof unit.row_corrections?.path === 'string' ? unit.row_corrections.path : null;
  const changesBlob = useLandingBlob(changesPath, `${scope}/${DIFFS}/${unit.id}/`);
  const correctionsBlob = useLandingBlob(correctionsPath, `${scope}/${ROW_CORRECTIONS}/${unit.id}/`);

  const steps = [];
  steps.push({
    id: 'source',
    render: (number) => (
      <SourceStep
        number={number}
        file={file}
        unit={unit}
        verdicts={unit.base_verdicts || []}
        fileBlock={fileBlock}
        onAdjust={() => onOpenSheet(unit.source.sheet, unit.source.region_id || null)}
        onGoToDocument={onBack}
      />
    ),
  });
  // The table's saved rules by id. The rule builder stores them as a map keyed by position
  // ("0", "1", ...), and a new table starts with an empty list.
  const rules = new Map(Object.values(table.ingestion_rules || {}).filter(Boolean).map((rule) => [rule.id, rule]));
  const draftProps = { editBlock, draftEpoch, runUnitChange };
  unitChainProcessIds(table).forEach((processId) => {
    if (processId === 'map') {
      steps.push({
        id: processId,
        render: (number) => (
          <UnitColumnsStep
            number={number}
            companyId={companyId}
            tableId={tableId}
            unit={unit}
            table={table}
            verdicts={verdictsOf(processId)}
            {...draftProps}
          />
        ),
      });
    } else if (processId === 'types') {
      steps.push({
        id: processId,
        render: (number) => (
          <DatesAndNumbersStep
            number={number}
            companyId={companyId}
            tableId={tableId}
            file={file}
            unit={unit}
            verdicts={verdictsOf(processId)}
            fileBlock={fileBlock}
            onFileChange={onFileChange}
          />
        ),
      });
    } else if (processId.startsWith('rule:') || processId.startsWith('skip:')) {
      // 'rule:{rule id}' or 'skip:{rule id}'.
      const separator = processId.indexOf(':');
      steps.push({
        id: processId,
        render: (number) => (
          <UnitRuleStep
            number={number}
            companyId={companyId}
            tableId={tableId}
            unit={unit}
            table={table}
            rule={rules.get(processId.slice(separator + 1))}
            kind={processId.slice(0, separator)}
            verdicts={verdictsOf(processId)}
            fileDeletionSet={fileDeletionSet}
            {...draftProps}
          />
        ),
      });
    } else if (processId.startsWith('classify:')) {
      // The chain names a classified column only while the table's classification is a
      // map (unitChainProcessIds), so its configuration is read from a map here. A
      // setting the backend refuses shows as the Classify process's fail verdict.
      const column = processId.slice('classify:'.length);
      steps.push({
        id: processId,
        render: (number) => (
          <UnitClassifyStep
            number={number}
            companyId={companyId}
            tableId={tableId}
            unit={unit}
            column={column}
            configuration={table.classification[column]}
            verdicts={verdictsOf(processId)}
            fileDeletionSet={fileDeletionSet}
            {...draftProps}
          />
        ),
      });
    } else if (processId === 'placeholders') {
      steps.push({
        id: processId,
        render: (number) => (
          <PlaceholdersStep number={number} unit={unit} table={table} verdicts={verdictsOf(processId)} />
        ),
      });
    } else if (processId === 'unit_date') {
      steps.push({
        id: processId,
        render: (number) => (
          <ReportingPeriodStep
            number={number}
            companyId={companyId}
            tableId={tableId}
            unit={unit}
            table={table}
            verdicts={verdictsOf(processId)}
            {...draftProps}
          />
        ),
      });
    } else {
      throw new Error(`The unit view has no step for the process '${processId}'.`);
    }
  });
  steps.push({
    id: 'preview',
    render: (number) => <PreviewStep number={number} unit={unit} table={table} />,
  });
  if (landed) {
    steps.push({
      id: 'diff',
      render: (number) => (
        <FileChangesStep
          number={number}
          table={table}
          record={unit.diff}
          blob={changesBlob}
          fileDeletionSet={fileDeletionSet}
        />
      ),
    });
    steps.push({
      id: 'overruled',
      render: (number) => (
        <OverruledValuesStep
          number={number}
          companyId={companyId}
          tableId={tableId}
          batchId={file.batch_id}
          record={unit.overruled}
          blob={changesBlob}
          fileDeletionSet={fileDeletionSet}
        />
      ),
    });
    steps.push({
      id: 'row_corrections',
      render: (number) => (
        <RowCorrectionsStep
          number={number}
          companyId={companyId}
          tableId={tableId}
          table={table}
          file={file}
          currentUid={currentUid}
          record={unit.row_corrections}
          blob={correctionsBlob}
          fileDeletionSet={fileDeletionSet}
        />
      ),
    });
  }

  // A verdict from a process with no step here (the reaper's, say) is still shown.
  const stepIds = new Set(steps.map((step) => step.id));
  const otherVerdicts = verdicts.filter((verdict) => !stepIds.has(verdict.process));

  // The status chip: a unit that is not ingested is named by what it is instead.
  let chip = { label: UNIT_LABEL[unit.status] || unit.status, color: 'warning', variant: 'filled' };
  if (unit.disposition === 'context') chip = { label: 'Header values', color: 'default', variant: 'outlined' };
  else if (unit.disposition === 'ignore') chip = { label: 'Ignored', color: 'default', variant: 'outlined' };
  else if (['Ready', 'Completed'].includes(unit.status)) chip = { ...chip, color: 'success' };
  else if (unit.status === 'Failed') chip = { ...chip, color: 'error' };
  else if (['Cleaning', 'AwaitingValidation'].includes(unit.status)) chip = { ...chip, color: 'info' };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component="button" variant="body2" underline="hover" onClick={onBack}>{file.name}</Link>
        <Typography variant="body2" color="text.primary" fontWeight={600}>{unit.name}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{unit.name}</Typography>
        <Chip size="small" label={chip.label} color={chip.color} variant={chip.variant} />
        <Typography variant="body2" color="text.secondary">
          {countNoun(unit.row_count || 0, 'row')}
          {unit.warnings ? ` · ${countNoun(unit.warnings, 'warning')}` : ''}
          {unit.approved ? ` · ${approvalLine(unit.approved, currentUid)}` : ''}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" disabled={!canDownload} onClick={download} sx={{ textTransform: 'none' }}>
          Download
        </Button>
        {ingest && unit.approved && (
          <Tooltip title={unapproveBlock || ''}>
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={!canUnapprove || approving}
                onClick={unapprove}
                sx={{ textTransform: 'none' }}
              >
                Un-approve to make changes
              </Button>
            </span>
          </Tooltip>
        )}
        {ingest && !unit.approved && (
          <Tooltip title={gate.join('; ') || editBlock || ''}>
            <span>
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={!canApprove || approving}
                onClick={approve}
                sx={{ textTransform: 'none' }}
              >
                {approving ? <CircularProgress size={16} /> : 'Approve'}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {!ingest && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {unit.disposition === 'context'
            ? 'This table is not ingested: its values are used by the other sheets. Change that under What to ingest.'
            : 'This table is not ingested: it is ignored. Change that under What to ingest.'}
        </Alert>
      )}
      {editBlock && !busy && <Alert severity="info" sx={{ mb: 1 }}>{editBlock}</Alert>}
      {ingest && gate.length > 0 && !busy && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={700}>Before this can be approved:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {gate.map((blocker) => <li key={blocker}><Typography variant="body2">{blocker}</Typography></li>)}
          </Box>
        </Alert>
      )}
      {busy && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 1 }}>
          Decode is applying your change…
        </Alert>
      )}
      {otherVerdicts.map((verdict, index) => (
        // A verdict holds no state of its own, so its position is a good enough key.
        // eslint-disable-next-line react/no-array-index-key
        <VerdictAlert key={index} verdict={verdict} />
      ))}

      {steps.map((step, index) => (
        <React.Fragment key={`${unit.id}:${step.id}`}>{step.render(index + 1)}</React.Fragment>
      ))}
    </Box>
  );
}
